import { useEffect, useMemo, useState } from "react";
import { useOutletContext, useNavigate, useParams } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { api, type Collection, type Project, type Workspace, type RequestSummary, type Webhook, type User } from "../lib/api";
import { InlineLoading } from "../components/Spinner";
import { useConfirm } from "../components/ConfirmDialog";
import { usePrompt } from "../components/PromptDialog";
import RequestBuilder from "../components/RequestBuilder";
import WebhookBuilder from "../components/WebhookBuilder";
import OpenApiImport from "../components/OpenApiImport";
import CurlImport from "../components/CurlImport";
import TabStrip from "../components/TabStrip";
import UserMenu from "../components/UserMenu";
import { useOpenTabs, closeOpenTabFor } from "../lib/openTabs";
import { useRenameRequest } from "../lib/renameRequest";
import { useResizable } from "../lib/useResizable";
import { useHotkey } from "../lib/keybindings";
import SortableTree from "../components/SortableTree";

export default function WorkspacePage() {
  const { workspace, project, user } = useOutletContext<{ workspace: Workspace; project: Project | null; user: User }>();
  const params = useParams();
  const requestId = params.requestId ?? null;
  const webhookId = params.webhookId ?? null;
  // Tab IDs are namespaced — `r:abc` for requests, `w:abc` for webhooks
  // — so a single open-tab list can hold both kinds.
  const activeTabId = webhookId ? `w:${webhookId}` : requestId ? `r:${requestId}` : null;
  const { tabs, close: closeTab, setTabs } = useOpenTabs(workspace.id, activeTabId);

  return (
    <div className="h-full flex min-h-0">
      <CollectionsPane workspace={workspace} project={project} user={user} selectedRequestId={requestId} selectedWebhookId={webhookId} />
      <div className="flex-1 min-w-[60px] flex flex-col">
        <TabStrip tabs={tabs} activeId={activeTabId} onClose={closeTab} onReorder={setTabs} />
        <div className="flex-1 min-h-0">
          {webhookId ? (
            <WebhookBuilder webhookId={webhookId} workspaceId={workspace.id} />
          ) : requestId ? (
            <RequestBuilder requestId={requestId} workspaceId={workspace.id} />
          ) : (
            <EmptyState workspace={workspace} />
          )}
        </div>
      </div>
    </div>
  );
}

function EmptyState({ workspace }: { workspace: Workspace }) {
  return (
    <div className="grid h-full place-items-center p-8">
      <div className="text-center max-w-md space-y-3">
        <div className="mx-auto h-14 w-14 grid place-items-center rounded-2xl bg-surface-2 text-text-faint">
          <svg width="28" height="28" viewBox="0 0 24 24" fill="none">
            <path d="M4 7a2 2 0 0 1 2-2h4l2 2h6a2 2 0 0 1 2 2v9a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2V7z" stroke="currentColor" strokeWidth="1.6" strokeLinejoin="round" />
          </svg>
        </div>
        <h2 className="text-lg font-semibold">{workspace.name}</h2>
        <p className="text-sm muted">
          Create a collection to start saving requests. Every save becomes a versioned
          revision you can roll back or audit.
        </p>
      </div>
    </div>
  );
}

function CollectionsPane({ workspace, project, user, selectedRequestId, selectedWebhookId }: { workspace: Workspace; project: Project | null; user: User; selectedRequestId: string | null; selectedWebhookId: string | null }) {
  const qc = useQueryClient();
  const nav = useNavigate();
  const confirm = useConfirm();
  const prompt = usePrompt();
  const { size: paneWidth, dragProps: paneDrag } = useResizable({
    axis: "x", initial: 288, min: 60, max: 560, storageKey: "rest.sidebar.collections.width",
  });
  // Mirror the sidebar's current width into a CSS variable so the
  // drag-clone (rendered by dnd-kit in a portal at document.body) can
  // match it. The clone's `width: var(...)` rule lives in styles.css.
  useEffect(() => {
    document.documentElement.style.setProperty("--rest-sidebar-width", `${paneWidth}px`);
  }, [paneWidth]);

  const collectionsQ = useQuery({
    queryKey: ["collections", workspace.id, project?.id ?? "_none"],
    queryFn: () => api.collections.list(workspace.id, project?.id ?? null),
  });

  // A project has exactly one collection from the UI's point of view. If
  // none exists yet for this project, auto-create one named "Requests" the
  // first time the panel loads.
  const ensureCol = useMutation({
    mutationFn: () => api.collections.create({ workspace_id: workspace.id, project_id: project?.id, name: "Requests" }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["collections", workspace.id, project?.id ?? "_none"] }),
  });
  useEffect(() => {
    if (collectionsQ.isSuccess && (collectionsQ.data?.collections.length ?? 0) === 0 && !ensureCol.isPending) {
      ensureCol.mutate();
    }
  }, [collectionsQ.isSuccess, collectionsQ.data, ensureCol]);

  // The primary collection is the first one in the list (for legacy projects
  // with multiple collections, the others remain in the DB but the UI
  // surfaces a single tree per project).
  const primary = collectionsQ.data?.collections[0] ?? null;

  // Realtime filter — matches request name + url + folder name.
  const [filter, setFilter] = useState("");

  // Cmd/Ctrl+B creates a new request in the active collection and jumps
  // straight to its editor. Mirrors the "+" button next to the filter.
  useHotkey("Mod+b", async () => {
    if (!primary) return;
    const r = await api.requests.create({ collection_id: primary.id, name: "Untitled request", method: "GET", url: "" });
    qc.invalidateQueries({ queryKey: ["requests", primary.id] });
    nav(`/r/${r.id}`);
  });

  return (
    <aside
      className="shrink-0 border-r border-border bg-surface flex flex-col min-h-0 relative overflow-hidden"
      style={{ width: paneWidth }}
    >
      {/* Height matches the tab strip (h-9) for a clean horizontal line
          across both columns. */}
      <div className="h-9 px-3 flex items-center justify-between border-b border-border">
        <div className="text-xs uppercase tracking-wider muted-strong font-semibold">Requests</div>
        {primary && (
          <NewMenu
            onNewRequest={async () => {
              const r = await api.requests.create({ collection_id: primary.id, name: "Untitled request", method: "GET", url: "" });
              qc.invalidateQueries({ queryKey: ["requests", primary.id] });
              nav(`/r/${r.id}`);
            }}
            onNewWebhook={async () => {
              const w = await api.webhooks.create({ collection_id: primary.id, name: "Untitled webhook" });
              qc.invalidateQueries({ queryKey: ["webhooks", primary.id] });
              nav(`/w/${w.id}`);
            }}
            onNewFolder={async () => {
              const name = await prompt({ title: "New folder", placeholder: "Folder name", confirmLabel: "Create", inputLabel: "Name" });
              if (name) {
                await api.folders.create({ collection_id: primary.id, parent_folder_id: null, name });
                qc.invalidateQueries({ queryKey: ["folders", primary.id] });
              }
            }}
          />
        )}
      </div>
      <div className="px-2 py-2 border-b border-border">
        <div className="relative">
          <span className="absolute left-2 top-1/2 -translate-y-1/2 text-text-faint pointer-events-none">
            <SmallSearchIcon />
          </span>
          <input
            className="input w-full !pl-7 !py-1 !text-sm"
            value={filter}
            onChange={(e) => setFilter(e.target.value)}
            placeholder="Filter requests…"
            aria-label="Filter requests"
            spellCheck={false}
          />
          {filter && (
            <button
              type="button"
              className="absolute right-1.5 top-1/2 -translate-y-1/2 h-5 w-5 grid place-items-center rounded text-text-faint hover:text-text hover:bg-surface-3"
              onClick={() => setFilter("")}
              aria-label="Clear filter"
            >×</button>
          )}
        </div>
      </div>
      <div className="flex-1 overflow-y-auto overflow-x-hidden scroll-pane min-h-0 min-w-0">
        {!primary && <div className="p-4"><InlineLoading /></div>}
        <ul className="py-1">
          {primary && (
            <CollectionNode
              key={primary.id}
              collection={primary}
              expanded={true}
              onToggle={() => { /* always expanded in flat mode */ }}
              selectedRequestId={selectedRequestId}
              selectedWebhookId={selectedWebhookId}
              filter={filter.trim().toLowerCase()}
              flat={true}
              onDelete={async () => { /* no-op in flat mode */ }}
              onCreateRequest={(folderId) => {
                api.requests.create({ collection_id: primary.id, name: "Untitled request", method: "GET", url: "" }).then(async (r) => {
                  if (folderId) {
                    await api.requests.update(r.id, { folder_id: folderId });
                  }
                  qc.invalidateQueries({ queryKey: ["requests", primary.id] });
                  qc.invalidateQueries({ queryKey: ["folders", primary.id] });
                  nav(`/r/${r.id}`);
                });
              }}
              onCreateWebhook={(folderId) => {
                api.webhooks.create({ collection_id: primary.id, name: "Untitled webhook", folder_id: folderId }).then(async (w) => {
                  qc.invalidateQueries({ queryKey: ["webhooks", primary.id] });
                  qc.invalidateQueries({ queryKey: ["folders", primary.id] });
                  nav(`/w/${w.id}`);
                });
              }}
            />
          )}
        </ul>
      </div>
      <div className="border-t border-border p-2 shrink-0">
        <UserMenu user={user} />
      </div>
      <div
        {...paneDrag}
        title="Drag to resize"
        aria-label="Resize sidebar"
        className="absolute top-0 right-[-3px] bottom-0 w-1.5 cursor-col-resize bg-transparent hover:bg-accent active:bg-accent-bright transition-colors z-10"
      />
    </aside>
  );
}

function SmallSearchIcon() {
  return (
    <svg width="13" height="13" viewBox="0 0 24 24" fill="none" aria-hidden="true">
      <circle cx="11" cy="11" r="6" stroke="currentColor" strokeWidth="1.8" />
      <path d="M16 16l4 4" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
    </svg>
  );
}

interface FolderNode {
  id: string;
  parent_folder_id: string | null;
  name: string;
  sort_order?: number;
}

function CollectionNode({
  collection,
  expanded,
  onToggle,
  selectedRequestId,
  selectedWebhookId,
  filter,
  flat = false,
  onDelete,
  onCreateRequest,
  onCreateWebhook,
}: {
  collection: Collection;
  expanded: boolean;
  onToggle: () => void;
  selectedRequestId: string | null;
  selectedWebhookId: string | null;
  filter: string;
  flat?: boolean;
  onDelete: () => void;
  onCreateRequest: (folderId: string | null) => void;
  onCreateWebhook: (folderId: string | null) => void;
}) {
  const qc = useQueryClient();
  const confirm = useConfirm();
  const prompt = usePrompt();
  const reqQ = useQuery({
    queryKey: ["requests", collection.id],
    queryFn: () => api.collections.requests(collection.id),
    enabled: expanded,
  });
  const foldersQ = useQuery({
    queryKey: ["folders", collection.id],
    queryFn: () => api.folders.list(collection.id),
    enabled: expanded,
  });
  // Webhooks live in the same sidebar tree as requests — merged into the
  // SortableTree below by folder_id + sort_order.
  const webhooksQ = useQuery({
    queryKey: ["webhooks", collection.id],
    queryFn: () => api.webhooks.listByCollection(collection.id),
    enabled: expanded,
  });

  const [openApiOpen, setOpenApiOpen] = useState(false);
  const [curlOpenFor, setCurlOpenFor] = useState<string | null | undefined>(undefined); // undefined=closed, null=root, "fld_…"=folder
  const rename = useMutation({
    mutationFn: (name: string) => api.collections.update(collection.id, { name }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["collections", collection.workspace_id] }),
  });
  const askRename = async () => {
    const name = await prompt({
      title: "Rename collection",
      initialValue: collection.name,
      placeholder: "Collection name",
      confirmLabel: "Rename",
      inputLabel: "Name",
    });
    if (name && name !== collection.name) rename.mutate(name);
  };

  const createFolder = useMutation({
    mutationFn: (args: { parentId: string | null; name: string }) => api.folders.create({ collection_id: collection.id, parent_folder_id: args.parentId, name: args.name }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["folders", collection.id] }),
  });
  const askCreateFolder = async (parentId: string | null) => {
    const name = await prompt({
      title: parentId ? "New subfolder" : "New folder",
      placeholder: "Folder name",
      confirmLabel: "Create",
      inputLabel: "Name",
    });
    if (name) createFolder.mutate({ parentId, name });
  };
  const removeFolder = useMutation({
    mutationFn: (id: string) => api.folders.remove(id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["folders", collection.id] });
      qc.invalidateQueries({ queryKey: ["requests", collection.id] });
    },
  });
  const renameFolder = useMutation({
    mutationFn: (args: { id: string; name: string }) => api.folders.update(args.id, { name: args.name }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["folders", collection.id] }),
  });
  const askRenameFolder = async (id: string, current: string) => {
    const name = await prompt({
      title: "Rename folder",
      initialValue: current,
      placeholder: "Folder name",
      confirmLabel: "Rename",
      inputLabel: "Name",
    });
    if (name && name !== current) renameFolder.mutate({ id, name });
  };

  const requests = useMemo(() => reqQ.data?.requests ?? [], [reqQ.data]);
  const folders = useMemo<FolderNode[]>(() => foldersQ.data?.folders ?? [], [foldersQ.data]);
  const webhooks = useMemo<Webhook[]>(() => webhooksQ.data?.webhooks ?? [], [webhooksQ.data]);

  // Download the collection as OpenAPI, used from the toolbar menu.
  const exportOpenApi = () => {
    (async () => {
      const res = await fetch(`/api/openapi/export/${collection.id}?download=1`, { credentials: "include" });
      if (!res.ok) return;
      const blob = await res.blob();
      const a = document.createElement("a");
      const url = URL.createObjectURL(blob);
      a.href = url;
      const cd = res.headers.get("content-disposition") ?? "";
      const m = /filename="([^"]+)"/.exec(cd);
      a.download = m ? m[1] : `${collection.name}.openapi.json`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    })();
  };

  return (
    <li>
      {!flat && (
        <div className="group flex items-center gap-1 px-2 py-1 hover:bg-surface-2">
          <button
            type="button"
            onClick={onToggle}
            className="h-5 w-5 grid place-items-center rounded text-text-faint hover:text-text"
            aria-label={expanded ? "Collapse" : "Expand"}
          >
            <Chevron expanded={expanded} />
          </button>
          <button
            type="button"
            onClick={onToggle}
            onDoubleClick={askRename}
            className="flex-1 min-w-0 text-left text-sm font-medium truncate text-text"
            title={collection.name}
          >
            {collection.name}
          </button>
          <button
            type="button"
            onClick={() => askCreateFolder(null)}
            className="h-7 w-7 sm:h-6 sm:w-6 grid place-items-center rounded text-text-faint sm:opacity-0 sm:group-hover:opacity-100 hover:text-text hover:bg-surface-3"
            title="New top-level folder"
          >
            <FolderPlusIcon />
          </button>
          <button
            type="button"
            onClick={() => onCreateRequest(null)}
            className="h-7 w-7 sm:h-6 sm:w-6 grid place-items-center rounded text-text-faint sm:opacity-0 sm:group-hover:opacity-100 hover:text-text hover:bg-surface-3"
            title="New request"
          >
            <PlusIcon />
          </button>
          <CollectionMenu
            onRename={askRename}
            onDelete={onDelete}
            onOpenApi={() => setOpenApiOpen(true)}
            onImportCurl={() => setCurlOpenFor(null)}
            onExportOpenApi={exportOpenApi}
          />
        </div>
      )}
      {openApiOpen && <OpenApiImport collectionId={collection.id} onClose={() => setOpenApiOpen(false)} />}
      {curlOpenFor !== undefined && (
        <CurlImport collectionId={collection.id} folderId={curlOpenFor} onClose={() => setCurlOpenFor(undefined)} />
      )}
      {expanded && (
        <SortableTree
          folders={folders}
          requests={requests}
          webhooks={webhooks.map((w) => ({ id: w.id, folder_id: w.folder_id, name: w.name, token: w.token, request_count: w.request_count, sort_order: w.sort_order }))}
          selectedRequestId={selectedRequestId}
          selectedWebhookId={selectedWebhookId}
          filter={filter}
          onMove={(updates) => {
            // The tree emits an update for EVERY visible node after a
            // drop. Dedupe against current state and only PATCH items
            // whose parent or sort_order actually changed. Webhooks
            // route to api.webhooks.move; requests to api.requests.move;
            // folders to api.folders.update.
            const folderById = new Map(folders.map((f) => [f.id, f]));
            const requestById = new Map(requests.map((r) => [r.id, r as RequestSummary & { folder_id?: string | null }]));
            const webhookById = new Map(webhooks.map((w) => [w.id, w]));
            const reqPatches: Array<{ id: string; folder_id: string | null; sort_order: number }> = [];
            const folPatches: Array<{ id: string; parent_folder_id: string | null; sort_order: number }> = [];
            const whPatches: Array<{ id: string; folder_id: string | null; sort_order: number }> = [];
            for (const u of updates) {
              if (u.kind === "request") {
                const cur = requestById.get(u.rawId);
                if (!cur) continue;
                const sameParent = (cur.folder_id ?? null) === u.parentId;
                const sameOrder = (cur.sort_order ?? 0) === u.sortOrder;
                if (sameParent && sameOrder) continue;
                reqPatches.push({ id: u.rawId, folder_id: u.parentId, sort_order: u.sortOrder });
              } else if (u.kind === "webhook") {
                const cur = webhookById.get(u.rawId);
                if (!cur) continue;
                const sameParent = (cur.folder_id ?? null) === u.parentId;
                const sameOrder = (cur.sort_order ?? 0) === u.sortOrder;
                if (sameParent && sameOrder) continue;
                whPatches.push({ id: u.rawId, folder_id: u.parentId, sort_order: u.sortOrder });
              } else {
                const cur = folderById.get(u.rawId);
                if (!cur) continue;
                const sameParent = (cur.parent_folder_id ?? null) === u.parentId;
                const sameOrder = (cur.sort_order ?? 0) === u.sortOrder;
                if (sameParent && sameOrder) continue;
                folPatches.push({ id: u.rawId, parent_folder_id: u.parentId, sort_order: u.sortOrder });
              }
            }
            // Optimistic cache updates so the UI doesn't snap back.
            if (reqPatches.length) {
              qc.setQueryData<{ requests: RequestSummary[] }>(["requests", collection.id], (old) => {
                if (!old) return old;
                const m = new Map(reqPatches.map((p) => [p.id, p]));
                return {
                  requests: old.requests.map((r) => {
                    const p = m.get(r.id);
                    return p
                      ? ({ ...(r as RequestSummary & { folder_id?: string | null }), folder_id: p.folder_id, sort_order: p.sort_order } as RequestSummary)
                      : r;
                  }),
                };
              });
            }
            if (whPatches.length) {
              qc.setQueryData<{ webhooks: Webhook[] }>(["webhooks", collection.id], (old) => {
                if (!old) return old;
                const m = new Map(whPatches.map((p) => [p.id, p]));
                return {
                  webhooks: old.webhooks.map((w) => {
                    const p = m.get(w.id);
                    return p ? { ...w, folder_id: p.folder_id, sort_order: p.sort_order } : w;
                  }),
                };
              });
            }
            if (folPatches.length) {
              qc.setQueryData<{ folders: FolderNode[] }>(["folders", collection.id], (old) => {
                if (!old) return old;
                const m = new Map(folPatches.map((p) => [p.id, p]));
                return {
                  folders: old.folders.map((f) => {
                    const p = m.get(f.id);
                    return p ? { ...f, parent_folder_id: p.parent_folder_id, sort_order: p.sort_order } : f;
                  }),
                };
              });
            }
            // Fire all PATCHes in parallel.
            Promise.all([
              ...reqPatches.map((p) => api.requests.move(p.id, { folder_id: p.folder_id, sort_order: p.sort_order })),
              ...whPatches.map((p) => api.webhooks.move(p.id, { folder_id: p.folder_id, sort_order: p.sort_order })),
              ...folPatches.map((p) => api.folders.update(p.id, { parent_folder_id: p.parent_folder_id, sort_order: p.sort_order })),
            ]).then(() => {
              qc.invalidateQueries({ queryKey: ["requests", collection.id] });
              qc.invalidateQueries({ queryKey: ["webhooks", collection.id] });
              qc.invalidateQueries({ queryKey: ["folders", collection.id] });
            });
          }}
          renderRow={(args) =>
            args.kind === "folder" ? (
              <FolderRowInner
                rawId={args.rawId}
                name={args.name}
                isExpanded={args.isExpanded}
                onToggle={args.onToggle}
                onAskRenameFolder={askRenameFolder}
                onAskCreateFolder={askCreateFolder}
                onCreateRequest={onCreateRequest}
                onCreateWebhook={onCreateWebhook}
                onImportCurl={(fid) => setCurlOpenFor(fid)}
                onDeleteFolder={async (fid, name) => {
                  const ok = await confirm({ title: `Delete folder "${name}"?`, description: "Requests and webhooks inside the folder move to the collection root.", danger: true });
                  if (ok) removeFolder.mutate(fid);
                }}
              />
            ) : args.kind === "webhook" ? (
              <WebhookRowInner
                rawId={args.rawId}
                name={args.name}
                token={args.token ?? ""}
                requestCount={args.requestCount ?? 0}
                selected={args.selected}
                workspaceId={collection.workspace_id}
                collectionId={collection.id}
              />
            ) : (
              <RequestRowInner
                rawId={args.rawId}
                name={args.name}
                method={args.method}
                url={args.url}
                selected={args.selected}
                workspaceId={collection.workspace_id}
              />
            )
          }
        />
      )}
    </li>
  );
}

function FolderRowInner({
  rawId, name, isExpanded, onToggle,
  onAskRenameFolder, onAskCreateFolder, onCreateRequest, onCreateWebhook, onDeleteFolder, onImportCurl,
}: {
  rawId: string;
  name: string;
  isExpanded: boolean;
  onToggle: () => void;
  onAskRenameFolder: (id: string, current: string) => void;
  onAskCreateFolder: (parentId: string | null) => void;
  onCreateRequest: (folderId: string | null) => void;
  onCreateWebhook: (folderId: string | null) => void;
  onDeleteFolder: (id: string, name: string) => void;
  onImportCurl: (folderId: string | null) => void;
}) {
  return (
    <div className="group flex items-center gap-2 px-1 py-0.5 rounded text-text w-full min-w-0">
      <button
        type="button"
        onClick={(e) => { e.stopPropagation(); onToggle(); }}
        onPointerDown={(e) => e.stopPropagation()}
        className="shrink-0 grid place-items-center rounded text-text-faint hover:text-text"
        aria-label={isExpanded ? "Collapse folder" : "Expand folder"}
        title={isExpanded ? "Collapse" : "Expand"}
      >
        <FolderIcon open={isExpanded} />
      </button>
      {/* Click the name to toggle, double-click to rename. */}
      <button
        type="button"
        onClick={(e) => { e.stopPropagation(); onToggle(); }}
        onDoubleClick={(e) => { e.stopPropagation(); onAskRenameFolder(rawId, name); }}
        className="flex-1 min-w-0 text-left truncate text-sm cursor-pointer select-none"
        title={name}
      >{name}</button>
      <RowMenu
        onAddRequest={() => onCreateRequest(rawId)}
        onAddWebhook={() => onCreateWebhook(rawId)}
        onAddFolder={() => onAskCreateFolder(`f:${rawId}`)}
        onRename={() => onAskRenameFolder(rawId, name)}
        onImportCurl={() => onImportCurl(rawId)}
        onDelete={() => onDeleteFolder(rawId, name)}
      />
    </div>
  );
}

function RequestRowInner({ rawId, name, method, url, selected, workspaceId }: { rawId: string; name: string; method?: string; url?: string; selected: boolean; workspaceId: string }) {
  const nav = useNavigate();
  const rename = useRenameRequest();
  const qc = useQueryClient();
  const confirm = useConfirm();
  const remove = useMutation({
    mutationFn: () => api.requests.remove(rawId),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["requests"] });
      qc.invalidateQueries({ queryKey: ["request", rawId] });
      // Drop the dead tab from URL + localStorage. If we were viewing
      // the deleted request, jump to the next-active tab the helper
      // picked (the one to the right, or the new rightmost). Falling
      // back to home only when nothing else is open.
      // Namespaced tab IDs (`r:` / `w:`) — webhooks share the list.
      const { search, nextActive } = closeOpenTabFor(workspaceId, `r:${rawId}`);
      if (selected) {
        let pathname = "/";
        if (nextActive) {
          if (nextActive.startsWith("w:")) pathname = `/w/${nextActive.slice(2)}`;
          else if (nextActive.startsWith("r:")) pathname = `/r/${nextActive.slice(2)}`;
          else pathname = `/r/${nextActive}`;
        }
        nav({ pathname, search });
      } else if (search !== window.location.search) {
        nav({ pathname: window.location.pathname, search }, { replace: true });
      }
    },
  });
  // Duplicate — copy the full request detail then create a sibling.
  // Client-side so we don't need a new server endpoint.
  const duplicate = useMutation({
    mutationFn: async () => {
      const { request: src } = await api.requests.get(rawId);
      const created = await api.requests.create({
        collection_id: src.collection_id,
        name: `${src.name} copy`,
        method: src.method,
        url: src.url,
        headers: src.headers,
        query: src.query,
        body_type: src.body_type,
        body: src.body,
      });
      // create() doesn't accept folder_id directly; if the source
      // lived inside a folder, move the new one to match.
      if (src.folder_id) {
        await api.requests.move(created.id, { folder_id: src.folder_id });
      }
      return created.id;
    },
    onSuccess: (newId) => {
      qc.invalidateQueries({ queryKey: ["requests"] });
      nav({ pathname: `/r/${newId}`, search: window.location.search });
    },
  });
  return (
    <div
      className={
        "group flex items-center gap-2 px-1 py-0.5 rounded text-sm w-full min-w-0 " +
        (selected
          ? "bg-accent-soft text-accent-bright"
          : "text-text-dim")
      }
      onClick={() => nav({ pathname: `/r/${rawId}`, search: window.location.search })}
      onDoubleClick={(e) => { e.stopPropagation(); rename(rawId, name); }}
      title={`${url || name} — double-click to rename`}
    >
      <span className={"method-tag shrink-0 " + (method ?? "GET").toLowerCase()}>{method ?? "GET"}</span>
      <span className="flex-1 min-w-0 truncate">{name}</span>
      <RowMenu
        onRename={() => rename(rawId, name)}
        onDuplicate={() => duplicate.mutate()}
        onDelete={async () => {
          const ok = await confirm({ title: `Delete "${name}"?`, description: "This permanently removes the request and all its revisions and runs.", danger: true });
          if (ok) remove.mutate();
        }}
      />
    </div>
  );
}

// Sidebar row for a webhook. Distinguished from request rows by the
// lightning-bolt glyph (no method tag — webhooks are inbound, multi-
// method by design). Click → navigate; double-click → rename.
function WebhookRowInner({ rawId, name, token, requestCount, selected, workspaceId, collectionId }: {
  rawId: string;
  name: string;
  token: string;
  requestCount: number;
  selected: boolean;
  workspaceId: string;
  collectionId: string;
}) {
  const nav = useNavigate();
  const qc = useQueryClient();
  const confirm = useConfirm();
  const prompt = usePrompt();
  const remove = useMutation({
    mutationFn: () => api.webhooks.remove(rawId),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["webhooks", collectionId] });
      const { search, nextActive } = closeOpenTabFor(workspaceId, `w:${rawId}`);
      if (selected) {
        let path = "/";
        if (nextActive) {
          if (nextActive.startsWith("w:")) path = `/w/${nextActive.slice(2)}`;
          else if (nextActive.startsWith("r:")) path = `/r/${nextActive.slice(2)}`;
          else path = `/r/${nextActive}`;
        }
        nav({ pathname: path, search });
      } else if (search !== window.location.search) {
        nav({ pathname: window.location.pathname, search }, { replace: true });
      }
    },
  });
  const duplicate = useMutation({
    mutationFn: async () => {
      // Duplicate: copy the user-facing config, mint a fresh token via
      // create(), then PATCH the response + forward fields to match.
      const { webhook: src } = await api.webhooks.get(rawId);
      const created = await api.webhooks.create({
        collection_id: src.collection_id,
        name: `${src.name} copy`,
        folder_id: src.folder_id,
      });
      await api.webhooks.update(created.id, {
        description: src.description,
        docs: src.docs,
        resp_status: src.resp_status,
        resp_content_type: src.resp_content_type,
        resp_body: src.resp_body,
        forward_enabled: src.forward_enabled,
        forward_url: src.forward_url,
        forward_method: src.forward_method,
        forward_transform: src.forward_transform,
      });
      return created.id;
    },
    onSuccess: (newId) => {
      qc.invalidateQueries({ queryKey: ["webhooks", collectionId] });
      nav({ pathname: `/w/${newId}`, search: window.location.search });
    },
  });
  const renameMut = useMutation({
    mutationFn: (next: string) => api.webhooks.update(rawId, { name: next }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["webhook", rawId] });
      qc.invalidateQueries({ queryKey: ["webhooks", collectionId] });
    },
  });
  const askRename = async () => {
    const next = await prompt({ title: "Rename webhook", placeholder: "Webhook name", initialValue: name, confirmLabel: "Rename", inputLabel: "Name" });
    if (next && next !== name) renameMut.mutate(next);
  };
  return (
    <div
      className={
        "group flex items-center gap-2 px-1 py-0.5 rounded text-sm w-full min-w-0 " +
        (selected ? "bg-accent-soft text-accent-bright" : "text-text-dim")
      }
      onClick={() => nav({ pathname: `/w/${rawId}`, search: window.location.search })}
      onDoubleClick={(e) => { e.stopPropagation(); askRename(); }}
      title={`/i/${token} — double-click to rename`}
    >
      <span className="shrink-0 text-accent" aria-hidden="true"><WebhookIcon /></span>
      <span className="flex-1 min-w-0 truncate">{name}</span>
      {requestCount > 0 && (
        <span className="chip bg-surface-3 muted-strong shrink-0 text-[10px]">{requestCount}</span>
      )}
      <RowMenu
        onRename={askRename}
        onDuplicate={() => duplicate.mutate()}
        onCopyUrl={async () => {
          try { await navigator.clipboard.writeText(`${window.location.origin}/i/${token}`); } catch { /* ignore */ }
        }}
        onDelete={async () => {
          const ok = await confirm({ title: `Delete "${name}"?`, description: "This permanently removes the webhook and every captured request.", danger: true });
          if (ok) remove.mutate();
        }}
      />
    </div>
  );
}

// Dropdown menu used by the top-of-sidebar "+" affordance. Three actions
// kept compact via on-click-outside dismiss. Anchored to the trigger so
// it lives in normal flow rather than a portal.
function NewMenu({ onNewRequest, onNewWebhook, onNewFolder }: { onNewRequest: () => void; onNewWebhook: () => void; onNewFolder: () => void }) {
  const [open, setOpen] = useState(false);
  return (
    <div className="relative">
      <button
        type="button"
        title="New…"
        aria-label="New…"
        onClick={() => setOpen((v) => !v)}
        onBlur={() => setTimeout(() => setOpen(false), 100)}
        className="h-6 w-6 grid place-items-center rounded text-text-faint hover:text-text hover:bg-surface-2"
      ><PlusIcon /></button>
      {open && (
        <div className="absolute right-0 top-full mt-1 card shadow-[var(--shadow-strong)] p-1 z-40 text-sm w-44" role="menu">
          <button
            className="w-full text-left px-2 py-1 rounded hover:bg-surface-2"
            onMouseDown={(e) => { e.preventDefault(); onNewRequest(); setOpen(false); }}
          >New request</button>
          <button
            className="w-full text-left px-2 py-1 rounded hover:bg-surface-2"
            onMouseDown={(e) => { e.preventDefault(); onNewWebhook(); setOpen(false); }}
          >New webhook</button>
          <button
            className="w-full text-left px-2 py-1 rounded hover:bg-surface-2"
            onMouseDown={(e) => { e.preventDefault(); onNewFolder(); setOpen(false); }}
          >New folder</button>
        </div>
      )}
    </div>
  );
}

function WebhookIcon() {
  // Lightning bolt — signals "inbound event", distinct from the request
  // method-tag glyph (which carries the HTTP verb on a request row).
  return (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" aria-hidden="true">
      <path d="M13 2L4 14h7l-1 8 9-12h-7l1-8z" fill="currentColor" />
    </svg>
  );
}

function FolderIcon({ open = false }: { open?: boolean }) {
  if (open) {
    return (
      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" aria-hidden="true" className="shrink-0">
        {/* Open folder: back panel + slanted front lid */}
        <path d="M3 7a2 2 0 0 1 2-2h4l2 2h8a2 2 0 0 1 2 2v1.5H3V7z" stroke="currentColor" strokeWidth="1.6" strokeLinejoin="round" />
        <path d="M3 10.5h18.5l-1.7 7.2A2 2 0 0 1 17.86 19H5.14a2 2 0 0 1-1.94-1.5L3 10.5z" stroke="currentColor" strokeWidth="1.6" strokeLinejoin="round" fill="currentColor" fillOpacity="0.08" />
      </svg>
    );
  }
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" aria-hidden="true" className="shrink-0">
      <path d="M3 7a2 2 0 0 1 2-2h4l2 2h8a2 2 0 0 1 2 2v8a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V7z" stroke="currentColor" strokeWidth="1.6" strokeLinejoin="round" fill="currentColor" fillOpacity="0.06" />
    </svg>
  );
}
function FolderPlusIcon() {
  return (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" aria-hidden="true" className="shrink-0">
      <path d="M3 7a2 2 0 0 1 2-2h4l2 2h8a2 2 0 0 1 2 2v8a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V7z" stroke="currentColor" strokeWidth="1.6" strokeLinejoin="round" />
      <path d="M12 11v5M9.5 13.5h5" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" />
    </svg>
  );
}

// Per-row overflow menu. Only the actions whose handlers were
// supplied show up — folder rows get Add request / Add folder, and
// request rows only get Rename / Delete (etc.).
function RowMenu({
  onAddRequest, onAddWebhook, onAddFolder, onRename, onDuplicate, onCopyUrl, onDelete, onImportCurl, onOpenApi, onExportOpenApi,
}: {
  onAddRequest?: () => void;
  onAddWebhook?: () => void;
  onAddFolder?: () => void;
  onRename?: () => void;
  onDuplicate?: () => void;
  onCopyUrl?: () => void;
  onDelete?: () => void;
  onImportCurl?: () => void;
  onOpenApi?: () => void;
  onExportOpenApi?: () => void;
}) {
  const [open, setOpen] = useState(false);
  return (
    <div className="relative" onPointerDown={(e) => e.stopPropagation()}>
      <button
        type="button"
        onClick={(e) => { e.stopPropagation(); setOpen((v) => !v); }}
        onBlur={() => setTimeout(() => setOpen(false), 100)}
        className="h-6 w-6 grid place-items-center rounded text-text-faint opacity-0 group-hover:opacity-100 hover:text-text hover:bg-surface-3"
        title="More"
        aria-label="More actions"
      >
        <DotsIcon />
      </button>
      {open && (
        <div className="absolute right-0 top-full mt-1 card shadow-[var(--shadow-strong)] p-1 z-40 text-sm w-48" role="menu">
          {onAddRequest && (
            <button
              className="w-full text-left px-2 py-1 rounded hover:bg-surface-2"
              onMouseDown={(e) => { e.preventDefault(); onAddRequest(); setOpen(false); }}
            >Add request</button>
          )}
          {onAddWebhook && (
            <button
              className="w-full text-left px-2 py-1 rounded hover:bg-surface-2"
              onMouseDown={(e) => { e.preventDefault(); onAddWebhook(); setOpen(false); }}
            >Add webhook</button>
          )}
          {onAddFolder && (
            <button
              className="w-full text-left px-2 py-1 rounded hover:bg-surface-2"
              onMouseDown={(e) => { e.preventDefault(); onAddFolder(); setOpen(false); }}
            >Add folder</button>
          )}
          {onRename && (
            <button
              className="w-full text-left px-2 py-1 rounded hover:bg-surface-2"
              onMouseDown={(e) => { e.preventDefault(); onRename(); setOpen(false); }}
            >Rename</button>
          )}
          {onDuplicate && (
            <button
              className="w-full text-left px-2 py-1 rounded hover:bg-surface-2"
              onMouseDown={(e) => { e.preventDefault(); onDuplicate(); setOpen(false); }}
            >Duplicate</button>
          )}
          {onCopyUrl && (
            <button
              className="w-full text-left px-2 py-1 rounded hover:bg-surface-2"
              onMouseDown={(e) => { e.preventDefault(); onCopyUrl(); setOpen(false); }}
            >Copy URL</button>
          )}
          {onImportCurl && (
            <button
              className="w-full text-left px-2 py-1 rounded hover:bg-surface-2"
              onMouseDown={(e) => { e.preventDefault(); onImportCurl(); setOpen(false); }}
            >Import curl…</button>
          )}
          {onOpenApi && (
            <button
              className="w-full text-left px-2 py-1 rounded hover:bg-surface-2"
              onMouseDown={(e) => { e.preventDefault(); onOpenApi(); setOpen(false); }}
            >Import OpenAPI…</button>
          )}
          {onExportOpenApi && (
            <button
              className="w-full text-left px-2 py-1 rounded hover:bg-surface-2"
              onMouseDown={(e) => { e.preventDefault(); onExportOpenApi(); setOpen(false); }}
            >Export OpenAPI</button>
          )}
          {onDelete && (
            <button
              className="w-full text-left px-2 py-1 rounded hover:bg-surface-2 text-danger"
              onMouseDown={(e) => { e.preventDefault(); onDelete(); setOpen(false); }}
            >Delete</button>
          )}
        </div>
      )}
    </div>
  );
}

// Back-compat alias — CollectionNode still uses this name for the
// collection-level overflow menu in non-flat mode.
const CollectionMenu = RowMenu;

function Chevron({ expanded }: { expanded: boolean }) {
  return (
    <svg width="12" height="12" viewBox="0 0 20 20" fill="none" style={{ transform: expanded ? "rotate(90deg)" : "none", transition: "transform 120ms ease" }}>
      <path d="M7 5l6 5-6 5" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}
function PlusIcon() {
  return (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" aria-hidden="true">
      <path d="M12 5v14M5 12h14" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
    </svg>
  );
}
function DotsIcon() {
  return (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" aria-hidden="true">
      <circle cx="5" cy="12" r="1.5" fill="currentColor" />
      <circle cx="12" cy="12" r="1.5" fill="currentColor" />
      <circle cx="19" cy="12" r="1.5" fill="currentColor" />
    </svg>
  );
}
